import { Component } from '@theme/component';

const CLOSE_DELAY = 350;

class SpinHeader extends Component {
  #closeTimer = null;
  #activeItem = null;
  #activeSubmenu = null;
  #mediaQuery = window.matchMedia('(min-width: 768px)');

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('click', this.#handleDocumentClick);

    for (const item of this.querySelectorAll('[data-spin-header-item]')) {
      item.addEventListener('mouseenter', () => this.#openItem(item));
      item.addEventListener('mouseleave', () => this.#scheduleClose());
      item.addEventListener('focusin', () => this.#openItem(item));
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) this.#scheduleClose();
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
    clearTimeout(this.#closeTimer);
  }

  toggleDrawer = () => {
    const isOpen = !this.classList.contains('spin-header--drawer-open');
    this.classList.toggle('spin-header--drawer-open', isOpen);
    const drawer = this.refs.drawer;
    if (drawer) {
      if (isOpen) {
        this.#updatePanelOffset();
        drawer.removeAttribute('inert');
      } else {
        drawer.setAttribute('inert', '');
      }
    }
    document.body.style.overflow = isOpen ? 'hidden' : '';
    this.refs.toggle?.setAttribute('aria-expanded', String(isOpen));
    this.refs.toggle?.setAttribute('aria-label', isOpen ? 'Close menu' : 'Open menu');
    if (!isOpen) this.#closeSubmenu();
  };

  closeDrawer = () => {
    if (!this.classList.contains('spin-header--drawer-open')) return;
    this.classList.remove('spin-header--drawer-open');
    document.body.style.overflow = '';
    this.refs.drawer?.setAttribute('inert', '');
    this.refs.toggle?.setAttribute('aria-expanded', 'false');
    this.refs.toggle?.setAttribute('aria-label', 'Open menu');
    this.#closeSubmenu();
    this.refs.toggle?.focus();
  };

  openSubmenu = (event) => {
    const button = event.target?.closest?.('[aria-controls]');
    if (!button) return;
    const id = button.getAttribute('aria-controls');
    if (!id) return;
    const submenu = this.querySelector(`#${id}`);
    if (!submenu) return;
    for (const sm of this.querySelectorAll('.spin-header__mobile-submenu.is-active')) {
      sm.classList.remove('is-active');
      sm.setAttribute('inert', '');
    }
    submenu.removeAttribute('inert');
    submenu.classList.add('is-active');
    this.#activeSubmenu = submenu;
    submenu.querySelector('button, a')?.focus();
  };

  closeSubmenuAction = () => {
    this.#closeSubmenu();
  };

  handleLeafClick = () => {
    this.closeDrawer();
  };

  #updatePanelOffset() {
    const offset = this.getBoundingClientRect().bottom;
    this.style.setProperty('--spin-header-panel-offset', `${Math.max(0, offset)}px`);
  }

  #closeSubmenu() {
    if (!this.#activeSubmenu) return;
    this.#activeSubmenu.classList.remove('is-active');
    this.#activeSubmenu.setAttribute('inert', '');
    this.#activeSubmenu = null;
  }

  #openItem(item) {
    if (!this.#mediaQuery.matches) return;
    clearTimeout(this.#closeTimer);
    if (this.#activeItem && this.#activeItem !== item) {
      this.#setOpenState(this.#activeItem, false);
    }
    this.#setOpenState(item, true);
    this.#activeItem = item;
  }

  #scheduleClose() {
    if (!this.#mediaQuery.matches) return;
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => this.#closeAll(), CLOSE_DELAY);
  }

  #closeAll() {
    if (this.#activeItem) {
      this.#setOpenState(this.#activeItem, false);
      this.#activeItem = null;
    }
  }

  #setOpenState(item, open) {
    item.classList.toggle('spin-header__primary-item--open', open);
    const trigger = item.querySelector('[data-spin-header-trigger]');
    trigger?.setAttribute('aria-expanded', String(open));
  }

  #handleKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.classList.contains('spin-header--drawer-open')) {
      if (this.#activeSubmenu) {
        this.#closeSubmenu();
      } else {
        this.closeDrawer();
      }
    } else if (this.#activeItem) {
      const trigger = this.#activeItem.querySelector('[data-spin-header-trigger]');
      this.#closeAll();
      trigger?.focus();
    }
  };

  #handleDocumentClick = (event) => {
    if (this.contains(event.target)) return;
    this.#closeAll();
  };
}

if (!customElements.get('spin-header')) {
  customElements.define('spin-header', SpinHeader);
}
